import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { Trans } from "react-i18next";
import CustomCallToActionPanel from "./CustomCallToActionPanel";

const ComicViewer = ({ comicImages, title }) => {
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Total de slides incluyendo la viñeta personalizada
  const totalSlides = comicImages.length + 1;
  
  // Verificar si estamos en la última slide (viñeta personalizada)
  const isLastSlide = currentSlideIndex === totalSlides - 1;

  // Funciones para pausar/reanudar autoplay
  const handlePause = () => {
    const swiperInstance = document.querySelector(".comic-viewer .swiper").swiper;
    swiperInstance.autoplay.stop();
    setIsPaused(true);
  };

  const handleResume = () => {
    const swiperInstance = document.querySelector(".comic-viewer .swiper").swiper;
    swiperInstance.autoplay.start();
    setIsPaused(false);
  };

  // Componente de efectos de fondo estilo cómic
  const ComicBackgroundEffects = ({ slideIndex }) => {
    // Diferentes patrones basados en el índice de la slide
    const patternType = slideIndex % 4;
    
    const renderPattern = () => {
      switch(patternType) {
        case 0: // Rayos radiales
          return [...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-blue-400 opacity-15"
              style={{
                width: "2px",
                height: "100%",
                left: `${8.33 * i}%`,
                transform: `rotate(${30 * i}deg)`,
                transformOrigin: "center bottom",
              }}
            />
          ));
        case 1: // Líneas diagonales
          return [...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-red-400 opacity-15"
              style={{
                width: "3px",
                height: "150%",
                left: `${10 * i}%`,
                transform: `rotate(45deg)`,
                transformOrigin: "center center",
              }}
            />
          ));
        case 2: // Líneas diagonales verdes
          return [...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-green-400 opacity-15"
              style={{
                width: "2px",
                height: "120%",
                left: `${10 * i}%`,
                transform: `rotate(${i % 2 === 0 ? 25 : -25}deg)`,
                transformOrigin: "center center",
              }}
            />
          ));
        case 3: // Zigzag pattern
          return [...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-purple-400 opacity-15"
              style={{
                width: "2px",
                height: "100%",
                left: `${12.5 * i}%`,
                transform: `rotate(${i % 2 === 0 ? 15 : -15}deg)`,
                transformOrigin: "center bottom",
              }}
            />
          ));
        default:
          return null;
      }
    };

    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden -z-10">
        {renderPattern()}
      </div>
    );
  };

  return (
    <div className="relative w-full h-full bg-black bg-opacity-10 comic-viewer">
      {isPaused && (
        <div
          className="absolute top-8 right-1/2 transform translate-x-1/2 bg-black bg-opacity-70 text-yellow-500 px-4 py-2 rounded-lg z-50 transition-opacity duration-500"
          style={{ pointerEvents: "none" }}
        >
          <Trans i18nKey="pause" />
        </div>
      )}

      {/* Área de pausa - solo activa en slides que no sean la viñeta personalizada */}
      {!isLastSlide && (
        <div
          className="absolute inset-0 flex justify-center items-center z-40"
          style={{ pointerEvents: "none" }}
        >
          <div
            className="w-3/4 h-3/4 cursor-pointer"
            style={{ pointerEvents: "auto" }}
            onMouseDown={handlePause}
            onMouseUp={handleResume}
            onTouchStart={handlePause}
            onTouchEnd={handleResume}
          ></div>
        </div>
      )}

      <Swiper
        modules={[Autoplay, Navigation]}
        slidesPerView={1}
        navigation
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        onSlideChange={(swiper) => {
          const activeIndex = swiper.activeIndex;
          setCurrentSlideIndex(activeIndex);
          const currentProgress = (activeIndex + 1) / totalSlides;
          setProgress(currentProgress);
        }}
        speed={1000}
      >
        {/* Slides de las viñetas del cómic */}
        {comicImages.map((src, index) => (
          <SwiperSlide key={index}>
            <div
              className="relative flex items-center justify-center"
              style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 10px)" }}
            >
              {/* Efectos de fondo estilo cómic */}
              <ComicBackgroundEffects slideIndex={index} />
              
              {/* Imagen de la viñeta */}
              <img
                src={src}
                alt={`Comic Slide ${index + 1}`}
                className="relative z-10 w-auto h-full object-contain mx-auto"
                style={{ maxHeight: "calc(var(--vh, 1vh) * 100 - 10px)" }}
              />
            </div>
          </SwiperSlide>
        ))}
        
        {/* Viñeta personalizada como última slide */}
        <SwiperSlide key="call-to-action">
          <div
            className="relative flex items-center justify-center"
            style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 10px)" }}
          >
            {/* Efectos de fondo estilo cómic, detrás de la viñeta personalizada */}
            <ComicBackgroundEffects slideIndex={totalSlides - 1} />

            {/* Viñeta personalizada */}
            <CustomCallToActionPanel onPause={handlePause} onResume={handleResume} />
          </div>
        </SwiperSlide>
      </Swiper>

      <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-800 z-50">
        <div className="h-full bg-yellow-500" style={{ width: `${progress * 100}%` }}></div>
      </div>
    </div>
  );
};

export default ComicViewer;
